import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from 'react-toastify';
import '@/styles/PropertyPagesModern.scss';
import * as Sentry from '@sentry/react';

/**
 * PropertyRoleMappingPage Component
 * 
 * Enhanced property role management supporting multiple role types and access levels.
 * Features:
 * - Assign Staff, Centre Managers, and Regional Operations Managers to properties
 * - Configure access levels (Full Access vs Read-Only)
 * - Real-time auto-save on selection change
 * - Visual feedback for save status
 * - Search/filter capabilities
 * - Enhanced accessibility
 */
const PropertyRoleMappingPage = () => {
    const { apiFetch } = useAuth();
    const [properties, setProperties] = useState([]);
    const [users, setUsers] = useState([]);
    const [roleMappings, setRoleMappings] = useState({});
    const [savedStatus, setSavedStatus] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedRoleType, setSelectedRoleType] = useState("CentreManager");

    const roleTypes = [
        { value: "CentreManager", label: "Centre Manager" },
        { value: "RegionalOperationsManager", label: "Regional Operations Manager" },
        { value: "Staff", label: "Staff" }
    ];

    const accessLevels = [
        { value: "FullAccess", label: "Full Access" },
        { value: "ReadOnly", label: "Read Only" }
    ];

    /**
     * Fetches all active properties.
     */
    const fetchProperties = async () => {
        try {
            const response = await apiFetch('/api/Property/all');
            const data = await response.json();
            if (data.success && data.data) {
                const activeProperties = [...data.data.filter(p => p.active)]
                    .sort((a, b) => (a.propertyName || '').localeCompare(b.propertyName || ''));
                setProperties(activeProperties);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching properties:', e);
            toast.error('Failed to load properties');
        }
    };

    /**
     * Fetches all users.
     */
    const fetchUsers = async () => {
        try {
            const response = await apiFetch(`/api/User/all`);
            const data = await response.json();
            if (data.success && data.data) {
                setUsers([...data.data].sort((a, b) =>
                    (`${a.firstName || ''} ${a.lastName || ''}`).localeCompare(`${b.firstName || ''} ${b.lastName || ''}`)
                ));
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching users:', e);
            toast.error('Failed to load users');
        }
    };

    /**
     * Fetches existing property role mappings.
     */
    const fetchMappings = async () => {
        try {
            const response = await apiFetch('/api/Property/role-mappings');
            const data = await response.json();
            if (data.success && data.data) {
                // Build roleMappings and savedStatus from API data
                const mappingsByProperty = {};
                const statusByProperty = {};
                
                data.data.forEach(mapping => {
                    const key = `${mapping.propertyID}_${mapping.roleType}`;
                    mappingsByProperty[key] = {
                        userID: mapping.userID,
                        accessLevel: mapping.accessLevel || "FullAccess"
                    };
                    statusByProperty[key] = "saved";
                });
                
                setRoleMappings(mappingsByProperty);
                setSavedStatus(statusByProperty);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching mappings:', e);
            toast.error('Failed to load mappings');
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await Promise.all([
                fetchProperties(),
                fetchUsers(),
                fetchMappings()
            ]);
            setIsLoading(false);
        };
        loadData();
    }, []);

    /**
     * Handles role assignment change with auto-save.
     */
    const handleAssignmentChange = async (propertyID, roleType, userID, accessLevel) => {
        const key = `${propertyID}_${roleType}`;
        
        setRoleMappings(prev => ({ 
            ...prev, 
            [key]: { userID, accessLevel: accessLevel || "FullAccess" } 
        }));
        setSavedStatus(prev => ({ ...prev, [key]: "saving" }));

        try {
            const response = await apiFetch('/api/Property/assign-role', {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ 
                    propertyID, 
                    userID, 
                    roleType,
                    accessLevel: accessLevel || "FullAccess"
                })
            });
            const result = await response.json();
            
            if (result.success) {
                setSavedStatus(prev => ({ ...prev, [key]: "saved" }));
                toast.success(`${roleTypes.find(r => r.value === roleType)?.label} assigned successfully`);
            } else {
                setSavedStatus(prev => ({ ...prev, [key]: "error" }));
                toast.error('Failed to assign role');
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error assigning role:', e);
            setSavedStatus(prev => ({ ...prev, [key]: "error" }));
            toast.error('Network error');
        }
    };

    /**
     * Handles access level change for a property/role combination.
     */
    const handleAccessLevelChange = async (propertyID, roleType, accessLevel) => {
        const key = `${propertyID}_${roleType}`;
        const currentMapping = roleMappings[key];
        
        if (!currentMapping || !currentMapping.userID) {
            toast.warning('Please assign a user first');
            return;
        }

        await handleAssignmentChange(propertyID, roleType, currentMapping.userID, accessLevel);
    };

    /**
     * Filters properties based on search term.
     */
    const filteredProperties = properties.filter(prop => 
        prop.propertyName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    /**
     * Gets users filtered by role type if needed.
     */
    const getUsersForRole = () => {
        // Could filter users by role here if needed
        return users;
    };

    return (
        <div className="view-properties-modern">
            <div className="properties-header">
                <div>
                    <h1>Property Role Mapping</h1>
                    <p style={{ margin: '8px 0 0 0', color: '#718096', fontSize: '0.95rem' }}>
                        Assign users to properties with specific roles and access levels
                    </p>
                </div>
            </div>

            {/* Role Type Selector */}
            <div className="role-type-selector" style={{ marginBottom: '20px' }}>
                <label style={{ fontWeight: '600', marginRight: '12px', color: '#2d3748' }}>
                    Select Role Type:
                </label>
                <select 
                    value={selectedRoleType}
                    onChange={e => setSelectedRoleType(e.target.value)}
                    className="role-type-select"
                    style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e0',
                        fontSize: '0.95rem',
                        cursor: 'pointer',
                        background: 'white'
                    }}
                >
                    {roleTypes.map(role => (
                        <option key={role.value} value={role.value}>
                            {role.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Search Bar */}
            <div className="search-container">
                <span className="search-icon">🔍</span>
                <input
                    type="text"
                    className="search-input"
                    placeholder="Search properties..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    aria-label="Search properties"
                />
            </div>

            {/* Properties Table */}
            {isLoading ? (
                <div className="loading-state">
                    <div className="spinner" aria-label="Loading data"></div>
                    <p>Loading property role mappings...</p>
                </div>
            ) : filteredProperties.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📋</div>
                    <h3>No Properties Found</h3>
                    <p>
                        {searchTerm 
                            ? "No properties match your search criteria." 
                            : "No active properties available."}
                    </p>
                </div>
            ) : (
                <div className="properties-table-container">
                    <table className="properties-table">
                        <thead>
                            <tr>
                                <th>Property</th>
                                <th>Assigned {roleTypes.find(r => r.value === selectedRoleType)?.label}</th>
                                <th>Access Level</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProperties.map((property) => {
                                const key = `${property.propertyID}_${selectedRoleType}`;
                                const mapping = roleMappings[key] || {};
                                
                                return (
                                    <tr key={property.propertyID}>
                                        <td className="property-name">{property.propertyName}</td>
                                        <td>
                                            <select
                                                className="property-manager-select"
                                                value={mapping.userID || ""}
                                                onChange={e => handleAssignmentChange(
                                                    property.propertyID, 
                                                    selectedRoleType, 
                                                    Number(e.target.value),
                                                    mapping.accessLevel
                                                )}
                                                aria-label={`Select ${roleTypes.find(r => r.value === selectedRoleType)?.label} for ${property.propertyName}`}
                                            >
                                                <option value="" disabled>
                                                    Select {roleTypes.find(r => r.value === selectedRoleType)?.label}...
                                                </option>
                                                {getUsersForRole(selectedRoleType).map(user => (
                                                    <option key={user.userID} value={user.userID}>
                                                        {user.firstName} {user.lastName}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <select
                                                className="access-level-select"
                                                value={mapping.accessLevel || "FullAccess"}
                                                onChange={e => handleAccessLevelChange(
                                                    property.propertyID,
                                                    selectedRoleType,
                                                    e.target.value
                                                )}
                                                disabled={!mapping.userID}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #cbd5e0',
                                                    fontSize: '0.875rem',
                                                    cursor: mapping.userID ? 'pointer' : 'not-allowed',
                                                    opacity: mapping.userID ? 1 : 0.5
                                                }}
                                            >
                                                {accessLevels.map(level => (
                                                    <option key={level.value} value={level.value}>
                                                        {level.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            {savedStatus[key] === "saved" && (
                                                <span className="status-badge active">
                                                    <span className="status-dot"></span>
                                                    Saved
                                                </span>
                                            )}
                                            {savedStatus[key] === "saving" && (
                                                <span className="status-badge" style={{ 
                                                    background: 'rgba(237, 137, 54, 0.15)', 
                                                    color: '#9c4221' 
                                                }}>
                                                    <span className="status-dot" style={{ background: '#ed8936' }}></span>
                                                    Saving...
                                                </span>
                                            )}
                                            {savedStatus[key] === "error" && (
                                                <span className="status-badge" style={{ 
                                                    background: 'rgba(245, 101, 101, 0.15)', 
                                                    color: '#9b2c2c' 
                                                }}>
                                                    <span className="status-dot" style={{ background: '#f56565' }}></span>
                                                    Error
                                                </span>
                                            )}
                                            {!savedStatus[key] && (
                                                <span className="status-badge inactive">
                                                    <span className="status-dot"></span>
                                                    Not Set
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default PropertyRoleMappingPage;
